import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { BillType } from '@features/bills/enums/billType'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { PayableSubjectType } from '@features/paymentReconciliationModal/types/payableSubjectType'

import { QueryKeys } from '../../../enums/queryKeys'
import { BankLineMatchesQueryParams, fetchSubjectAssociations } from '../query-api'

interface UseBankLineSubjectAssociationsProps extends Omit<BankLineMatchesQueryParams, 'accountId'> {
  subjectId?: string
  subjectType?: PayableSubjectType
}

const subjectToSubjectAssociationsQueryKeyMap: Record<string, QueryKeys> = {
  [InvoiceType.Invoice]: QueryKeys.InvoiceSubjectAssociations,
  [InvoiceType.ExternalInvoice]: QueryKeys.InvoiceSubjectAssociations,
  [BillType.Bill]: QueryKeys.BillSubjectAssociations,
}

export const useBankLineSubjectAssociations = ({ subjectId, subjectType }: UseBankLineSubjectAssociationsProps) => {
  const queryKey = useMemo(() => {
    if (!subjectType) {
      return null
    }

    return subjectToSubjectAssociationsQueryKeyMap[subjectType] ?? null
  }, [subjectType])

  const { data: subjectAssociations, isLoading } = useQuery(
    [queryKey, subjectId],
    () => fetchSubjectAssociations(subjectId as string, subjectType as PayableSubjectType),
    {
      enabled: !!subjectId,
    },
  )

  return {
    bankLineSubjectAssociations: subjectAssociations?.bankLineSubjectAssociations || [],
    isLoading,
  }
}
